// OtpInput.tsx
import React, { useEffect, useRef } from 'react';

type OtpInputProps = {
    value: string;
    onChange: (value: string) => void;
    length?: number; // optional prop to customize length, defaults to 4
};

/**
 * Convert any Persian digits in the input string to English digits.
 */
const convertPersianToEnglish = (input: string) => {
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    return input.replace(/[۰-۹]/g, (c) => {
        const index = persianDigits.indexOf(c);
        return index > -1 ? String(index) : c;
    });
};

const OtpInput: React.FC<OtpInputProps> = ({
    value,
    onChange,
    length = 4, // default to 4 inputs
}) => {
    // refs to each input
    const inputRefs = useRef<HTMLInputElement[]>([]);

    // autofocus first input on mount
    useEffect(() => {
        inputRefs.current[0]?.focus();
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
        let val = convertPersianToEnglish(e.target.value);
        // allow only digits or empty
        if (val !== '' && !/^\d$/.test(val)) return;

        const otpArr = value.split('').slice(0, length);
        otpArr[idx] = val;
        const newVal = otpArr.join('').padEnd(length, '');
        onChange(newVal);

        // move focus forward if digit entered
        if (val && idx < length - 1) {
            inputRefs.current[idx + 1]?.focus();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
        // on backspace in empty field, move focus back
        if (e.key === 'Backspace' && !value[idx] && idx > 0) {
            inputRefs.current[idx - 1]?.focus();
        }
    };

    return (
        <div
            className="flex gap-4 justify-center"
            style={{ direction: 'ltr' }}
        >
            {Array.from({ length }).map((_, i) => (
                <input
                    key={i}
                    ref={(el) => { if (el) inputRefs.current[i] = el; }}
                    type="tel"
                    inputMode="numeric"
                    maxLength={1}
                    value={value[i] || ''}
                    onChange={(e) => handleChange(e, i)}
                    onKeyDown={(e) => handleKeyDown(e, i)}
                    className="w-12 h-12 text-center text-lg border-b-2 focus:outline-none leading-none"
                    style={{
                        borderBottomColor: value[i] ? 'black' : 'lightgray',
                    }}
                />
            ))}
        </div>
    );
};

export default OtpInput;
